// Fetches SYNOP observations from ogimet.com getsynop endpoint (CSV format)

import { StationSeries, Observation } from './StationSeries.js';
import { wmoNZOscar } from './wmo-stations.js';
import { parseSynopCsv } from '../../../shared/synop-parser.js';

const BASE_URL = 'https://www.ogimet.com/cgi-bin/getsynop';

export class OgimetSynopProvider {

  constructor(base = BASE_URL, fetchFn = fetch.bind(globalThis)) {
    this.base = base;
    this.fetchFn = fetchFn;
  }

  async fetch(start, end, bbox, now = Date.now()) {
    const csv = await this.fetchData(start, end, bbox);
    const stations = this.parseCsv(csv);
    return this.#filterStations(stations, start, end, bbox);
  }

  async fetchData(start, end, bbox) {
    const MS_24H = 24 * 60 * 60 * 1000;
    if (end - start <= MS_24H) return this.#fetchChunk(start, end, bbox);
    const chunks = [];
    for (let cur = start; cur < end; cur += MS_24H)
      chunks.push([cur, Math.min(cur + MS_24H, end)]);
    const parts = await Promise.all(chunks.map(([s, e]) => this.#fetchChunk(s, e, bbox)));
    return parts.join('\n');
  }

  async #fetchChunk(start, end, bbox) {
    const b = `${this.#fmtDate(start)}&end=${this.#fmtDate(end)}`;
    const geo = `lat=[${bbox.minLat},${bbox.maxLat}]&lon=[${bbox.minLon},${bbox.maxLon}]`;
    const url = `${this.base}?begin=${b}&${geo}`;
    const res = await this.fetchFn(url);
    if (!res.ok) throw new Error(`OgimetSynop: HTTP ${res.status}`);
    return res.text();
  }

  async fetchDataNZ(start, end) {
    const url = `${this.base}?block=93&begin=${this.#fmtDate(start)}&end=${this.#fmtDate(end)}`;
    const res = await this.fetchFn(url);
    if (!res.ok) throw new Error(`OgimetSynop: HTTP ${res.status}`);
    return res.text();
  }

  // CSV columns: WMO_IND,YEAR,MONTH,DAY,HOUR,MIN,REPORT
  // REPORT format: "AAXX DDHHiw IIiii iRiXhVV Nddff 1SnTTT 2SnTd 3PPPP 4PPPP ..."
  parseCsv(csv) {
    return parseSynopCsv(csv, wmoNZOscar, 'ogimet-synop')
      .map(s => {
        const series = new StationSeries(s.id, s.name, s.lat, s.lon, s.source);
        s.observations.forEach(o => series.addObservation(new Observation(o.t, o.dd, o.ff, o.tt, o.mslp)));
        return series;
      });
  }

  #filterStations(stations, start, end, bbox) {
    return stations
      .filter(s => s.lat != null && s.lon != null)
      .filter(s => s.lat >= bbox.minLat && s.lat <= bbox.maxLat && s.lon >= bbox.minLon && s.lon <= bbox.maxLon)
      .map(s => { s.observations = s.observations.filter(o => o.t >= start && o.t <= end); return s; })
      .filter(s => s.observations.length > 0);
  }

  #fmtDate(ms) {
    const d = new Date(ms);
    return `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}${String(d.getUTCDate()).padStart(2, '0')}${String(d.getUTCHours()).padStart(2, '0')}${String(d.getUTCMinutes()).padStart(2, '0')}`;
  }
}
